/**
 * Cloud implementation of the shared WallPlaybackApi — a site's Live Wall in Recorded mode.
 *
 * Node-scoped like the other adapters: every call binds the selected node, and with none
 * selected it returns empty rather than throwing. Cloud FE → Cloud BE → tunnel → node backend,
 * the same route the cloud Recordings page plays through, so per-camera RBAC on the node applies.
 */
import type { WallPlaybackApi, CameraRef, Group, RecordingSegment } from '@trinetra/shared';
import {
  getNodeCameras,
  getNodeGroupsForFilter,
  getRecordingSegments,
  getRecordingDates,
} from '../services/api.service';
import { selectedNodeId } from './node';

const toArray = (res: any, key?: string): any[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (key && Array.isArray(res?.[key])) return res[key];
  if (key && Array.isArray(res?.data?.[key])) return res.data[key];
  return [];
};

export const cloudWallPlaybackApi: WallPlaybackApi = {
  async listCameras(): Promise<CameraRef[]> {
    const id = selectedNodeId();
    if (!id) return [];
    return toArray(await getNodeCameras(id), 'cameras') as CameraRef[];
  },

  async listGroups(): Promise<Group[]> {
    const id = selectedNodeId();
    if (!id) return [];
    return toArray(await getNodeGroupsForFilter(id), 'groups') as Group[];
  },

  async getSegments(cameraId, fromIso, toIso): Promise<RecordingSegment[]> {
    const id = selectedNodeId();
    if (!id) return [];
    return toArray(await getRecordingSegments(id, cameraId, fromIso, toIso), 'segments') as RecordingSegment[];
  },

  async getAvailableDates(cameraId): Promise<string[]> {
    const id = selectedNodeId();
    if (!id) return [];
    return toArray(await getRecordingDates(id, cameraId), 'dates').map(String);
  },

  /**
   * The token rides in the QUERY STRING: hls.js' segment requests cannot carry a header through
   * the cloud's rewritten chunk URLs. Same URL the cloud Recordings page and Search build.
   */
  playbackUrl(cameraId, fromIso, toIso) {
    const id = selectedNodeId();
    const token = localStorage.getItem('cloud_token');
    if (!id || !cameraId || !token) return null;
    const qs = new URLSearchParams({ start_time: fromIso, end_time: toIso, token }).toString();
    return `${window.location.origin}/api/nodes/${id}/recordings/playlist/${cameraId}?${qs}`;
  },

  playbackAuthToken() {
    return localStorage.getItem('cloud_token');
  },

  tunnelled: true,
};
